import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MapPin, Mountain, Route, Activity, Clock, Users, TrendingUp, Trophy } from 'lucide-react';
import type { TrailSegment } from '@/hooks/useTrails';

interface TrailsListProps {
  segments?: TrailSegment[];
  routes?: any[];
  runs?: any[];
  activeTab: string;
  isLoading?: boolean;
  onZoomToLocation?: (coordinates: [number, number], zoomLevel?: number) => void;
  onItemSelect?: (id: string, type: 'segment' | 'route' | 'run', name: string) => void;
}

const getDifficultyColor = (difficulty?: string) => {
  switch (difficulty) {
    case 'easy': return 'bg-green-500';
    case 'moderate': return 'bg-yellow-500';
    case 'hard': return 'bg-red-500';
    case 'extreme': return 'bg-red-900';
    default: return 'bg-gray-500';
  }
};

const TrailsList: React.FC<TrailsListProps> = ({
  segments = [],
  routes = [],
  runs = [],
  activeTab,
  isLoading = false,
  onZoomToLocation,
  onItemSelect
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Group segments by trail name for better organization
  const groupedSegments = segments.reduce((acc, segment) => {
    const trailName = segment.trail_name || 'Unknown Trail';
    if (!acc[trailName]) {
      acc[trailName] = [];
    }
    acc[trailName].push(segment);
    return acc;
  }, {} as Record<string, TrailSegment[]>);

  // Helper function to get center coordinates from geometry
  const getGeometryCenter = (geometry: any): [number, number] => {
    if (!geometry || !geometry.coordinates) return [-116.7, 34.1];
    
    if (geometry.type === 'Point') {
      return [geometry.coordinates[0], geometry.coordinates[1]];
    } else if (geometry.type === 'LineString') {
      const coords = geometry.coordinates;
      const midIndex = Math.floor(coords.length / 2);
      return [coords[midIndex][0], coords[midIndex][1]];
    }
    return [-116.7, 34.1];
  };

  const renderTrailsList = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Mountain className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Trail Segments ({segments.length})</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-3 pr-4">
          {Object.entries(groupedSegments).map(([trailName, trailSegments]) => (
            <Card 
              key={trailName} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                const firstSegment = trailSegments[0];
                if (firstSegment.geometry && onZoomToLocation) {
                  const center = getGeometryCenter(firstSegment.geometry);
                  onZoomToLocation(center, 14);
                }
                if (onItemSelect) {
                  onItemSelect(firstSegment.id, 'segment', trailName);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm leading-tight">{trailName}</h4>
                  <Badge 
                    variant="secondary" 
                    className={`text-xs text-white ${getDifficultyColor(trailSegments[0]?.difficulty)}`}
                  >
                    {trailSegments[0]?.difficulty || 'unknown'}
                  </Badge>
                </div>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex items-center gap-4">
                    <span>{trailSegments.length} segments</span>
                    {trailSegments[0]?.length_miles && (
                      <span>{trailSegments[0].length_miles.toFixed(1)} miles</span>
                    )}
                  </div>
                  
                  {trailSegments[0]?.surface_type && (
                    <div>Surface: {trailSegments[0].surface_type}</div>
                  )}
                  
                  {trailSegments[0]?.forest_name && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {trailSegments[0].forest_name}
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 w-full text-xs"
                  onClick={() => navigate(`/trail/${trailSegments[0].trail_id}`)}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
          
          {segments.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <Mountain className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No trail segments found</p>
                <p className="text-xs text-muted-foreground mt-1">Try adjusting your search filters</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const renderRoutesList = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Route className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Routes ({routes.length})</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-3 pr-4">
          {routes.map((route) => (
            <Card 
              key={route.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                if (route.geometry && onZoomToLocation) {
                  const center = getGeometryCenter(route.geometry);
                  onZoomToLocation(center, 12);
                }
                if (onItemSelect) {
                  onItemSelect(route.id, 'route', route.name);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{route.name}</h4>
                  {route.difficulty && (
                    <Badge 
                      variant="secondary" 
                      className={`text-xs text-white ${getDifficultyColor(route.difficulty)}`}
                    >
                      {route.difficulty}
                    </Badge>
                  )}
                </div>
                
                {route.description && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {route.description}
                  </p>
                )}
                
                <div className="text-xs text-muted-foreground">
                  {route.length_miles && (
                    <span>{route.length_miles.toFixed(1)} miles</span>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 w-full text-xs"
                  onClick={() => navigate(`/route/${route.id}`)}
                >
                  View Route
                </Button>
              </CardContent>
            </Card>
          ))}
          
          {routes.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <Route className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No routes found</p>
                <p className="text-xs text-muted-foreground mt-1">Create your first route from trail segments</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const renderRunsList = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Recent Runs ({runs.length})</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-3 pr-4">
          {runs.map((run) => (
            <Card 
              key={run.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                if (run.geometry && onZoomToLocation) {
                  const center = getGeometryCenter(run.geometry);
                  onZoomToLocation(center, 15);
                }
                if (onItemSelect) {
                  onItemSelect(run.id, 'run', `${run.equipment_type} Run`);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm">{run.equipment_type} Run</h4>
                  <Badge variant="outline" className="text-xs">
                    {new Date(run.created_at).toLocaleDateString()}
                  </Badge>
                </div>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  {run.distance_miles && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {run.distance_miles.toFixed(1)} miles
                    </div>
                  )}
                  
                  {run.duration_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {Math.floor(run.duration_minutes / 60)}h {run.duration_minutes % 60}m
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 w-full text-xs"
                  onClick={() => navigate(`/runs/${run.id}`)}
                >
                  View Run Details
                </Button>
              </CardContent>
            </Card>
          ))}
          
          {runs.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No runs found</p>
                <p className="text-xs text-muted-foreground mt-1">Upload your first GPX file to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const renderCommunityStats = () => (
    <div className="space-y-6">
      {/* Trending Trails */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-primary" />
            Trending This Week
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Show real trending data based on segments with most recent activity */}
          {Object.entries(groupedSegments).slice(0, 3).map(([trailName, trailSegments]) => (
            <div key={trailName} className="flex justify-between items-center">
              <span className="text-sm font-medium truncate">{trailName}</span>
              <Badge variant="outline" className="text-xs">
                {trailSegments.length} segs
              </Badge>
            </div>
          ))}
          
          {segments.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No trending data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Community Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-blue-500" />
            Community Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Trail Segments</span>
            <span className="text-sm font-medium">{segments.length.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Trails Mapped</span>
            <span className="text-sm font-medium">{Object.keys(groupedSegments).length.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Routes Created</span>
            <span className="text-sm font-medium">{routes.length.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">GPX Uploads</span>
            <span className="text-sm font-medium">{runs.length.toLocaleString()}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAllResults = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">All Results ({segments.length + routes.length + runs.length})</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-4 pr-4">
          {/* Trail Segments Section */}
          {segments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Mountain className="h-4 w-4" />
                Trail Segments ({segments.length})
              </h4>
              <div className="space-y-2">
                {Object.entries(groupedSegments).slice(0, 3).map(([trailName, trailSegments]) => (
                  <Card 
                    key={trailName} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      const firstSegment = trailSegments[0];
                      if (firstSegment.geometry && onZoomToLocation) {
                        const center = getGeometryCenter(firstSegment.geometry);
                        onZoomToLocation(center, 14);
                      }
                      if (onItemSelect) {
                        onItemSelect(firstSegment.id, 'segment', trailName);
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-sm">{trailName}</h5>
                        <Badge variant="secondary" className="text-xs">Trail</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {trailSegments.length} segments • {trailSegments[0]?.forest_name}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Routes Section */}
          {routes.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Route className="h-4 w-4" />
                Routes ({routes.length})
              </h4>
              <div className="space-y-2">
                {routes.slice(0, 3).map((route) => (
                  <Card 
                    key={route.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      if (route.geometry && onZoomToLocation) {
                        const center = getGeometryCenter(route.geometry);
                        onZoomToLocation(center, 12);
                      }
                      if (onItemSelect) {
                        onItemSelect(route.id, 'route', route.name);
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-sm">{route.name}</h5>
                        <Badge variant="secondary" className="text-xs">Route</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {route.description || `${route.length_miles?.toFixed(1) || '?'} miles`}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Runs Section */}
          {runs.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Recent Runs ({runs.length})
              </h4>
              <div className="space-y-2">
                {runs.slice(0, 3).map((run) => (
                  <Card 
                    key={run.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      if (run.geometry && onZoomToLocation) {
                        const center = getGeometryCenter(run.geometry);
                        onZoomToLocation(center, 15);
                      }
                      if (onItemSelect) {
                        onItemSelect(run.id, 'run', `${run.equipment_type} Run`);
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-sm">{run.equipment_type} Run</h5>
                        <Badge variant="secondary" className="text-xs">Run</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {run.distance_miles?.toFixed(1) || '?'} miles • {new Date(run.created_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {segments.length === 0 && routes.length === 0 && runs.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No results found</p>
                <p className="text-xs text-muted-foreground mt-1">Try adjusting your search query</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const renderActivityList = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Community Activity ({routes.length + runs.length})</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-4 pr-4">
          {/* Routes Section */}
          {routes.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Route className="h-4 w-4" />
                User Routes ({routes.length})
              </h4>
              <div className="space-y-2">
                {routes.map((route) => (
                  <Card 
                    key={route.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      if (route.geometry && onZoomToLocation) {
                        const center = getGeometryCenter(route.geometry);
                        onZoomToLocation(center, 12);
                      }
                      if (onItemSelect) {
                        onItemSelect(route.id, 'route', route.name);
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-sm">{route.name}</h4>
                        {route.difficulty && (
                          <Badge 
                            variant="secondary" 
                            className={`text-xs text-white ${getDifficultyColor(route.difficulty)}`}
                          >
                            {route.difficulty}
                          </Badge>
                        )}
                      </div>
                      
                      {route.description && (
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                          {route.description}
                        </p>
                      )}
                      
                      <div className="text-xs text-muted-foreground">
                        {route.length_miles && (
                          <span>{route.length_miles.toFixed(1)} miles</span>
                        )}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full text-xs"
                        onClick={() => navigate(`/route/${route.id}`)}
                      >
                        View Route
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Runs Section */}
          {runs.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                GPX Uploads ({runs.length})
              </h4>
              <div className="space-y-2">
                {runs.map((run) => (
                  <Card 
                    key={run.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      if (run.geometry && onZoomToLocation) {
                        const center = getGeometryCenter(run.geometry);
                        onZoomToLocation(center, 15);
                      }
                      if (onItemSelect) {
                        onItemSelect(run.id, 'run', `${run.equipment_type} Run`);
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-sm">{run.equipment_type} Run</h4>
                        <Badge variant="outline" className="text-xs">
                          {new Date(run.created_at).toLocaleDateString()}
                        </Badge>
                      </div>
                      
                      <div className="text-xs text-muted-foreground space-y-1">
                        {run.distance_miles && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {run.distance_miles.toFixed(1)} miles
                          </div>
                        )}
                        
                        {run.duration_minutes && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {Math.floor(run.duration_minutes / 60)}h {run.duration_minutes % 60}m
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full text-xs"
                        onClick={() => navigate(`/runs/${run.id}`)}
                      >
                        View Run Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {routes.length === 0 && runs.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center">
                <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No activity found</p>
                <p className="text-xs text-muted-foreground mt-1">Create routes or upload GPX files to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const renderFeaturesList = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Features & Points of Interest</h3>
      </div>
      
      <ScrollArea className="h-[500px]">
        <div className="space-y-3 pr-4">
          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <MapPin className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Features coming soon</p>
              <p className="text-xs text-muted-foreground mt-1">Points of interest, obstacles, campsites, and more will be available here</p>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );

  switch (activeTab) {
    case 'all':
      return renderAllResults();
    case 'trails':
      return renderTrailsList();
    case 'activity':
      return renderActivityList();
    case 'features':
      return renderFeaturesList();
    default:
      return renderAllResults();
  }
};

export default TrailsList;